import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ResearchReportView } from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import { isInsufficientCredits } from '../api/client';
import {
  buildDeck,
  getReport,
  reportKeys,
  writeReport,
} from '../api/research-rooms';
import { invalidateCredits } from '../credits/use-credits';

/** While Floto writes, how often to look again — a stream may have dropped,
 *  and the report still lands in Firestore. */
const PENDING_POLL_MS = 4000;

/**
 * A room's final report and everything its page draws. Only read once the
 * session has ended; checked again every few seconds while it is being
 * written, so a closed tab or a second window still sees it land.
 */
export function useReport(roomId: string | null, enabled = true) {
  return useQuery({
    enabled: Boolean(roomId) && enabled,
    queryFn: () => getReport(roomId!),
    queryKey: reportKeys.forRoom(roomId ?? ''),
    // Settled for good once written — and every read signs the screenshot's
    // URL afresh, so a refetch on focus (opening the print dialog is one)
    // would swap every image's src mid-print and print them blank. Only a
    // report still being written is read again.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    refetchInterval: (query) =>
      query.state.data?.report?.status === 'pending' ? PENDING_POLL_MS : false,
  });
}

/** Writes the report again — after a failure. Frames update the cache as
 *  they arrive; the read after settles whatever the stream missed. */
export function useRewriteReport(roomId: string | null) {
  const queryClient = useQueryClient();
  const key = reportKeys.forRoom(roomId ?? '');
  return useMutation({
    mutationFn: () =>
      writeReport(roomId!, (event) => {
        if (event.type !== 'report') return;
        queryClient.setQueryData<ResearchReportView>(key, (current) =>
          current ? { ...current, report: event.report } : current,
        );
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/** Starts a file download from a link, without leaving the page. */
function download(url: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Makes the report into a presentation deck, and downloads it once built.
 * Frames keep the report's cached deck state current, so every button on
 * the page shows it building; the read after settles the stored link.
 */
export function useBuildDeck(roomId: string | null) {
  const queryClient = useQueryClient();
  const key = reportKeys.forRoom(roomId ?? '');
  return useMutation({
    mutationFn: () =>
      buildDeck(roomId!, (event) => {
        if (event.type !== 'deck') return;
        queryClient.setQueryData<ResearchReportView>(key, (current) =>
          current?.report
            ? {
                ...current,
                report: { ...current.report, deck: event.deck },
                ...(event.url ? { deckUrl: event.url } : {}),
              }
            : current,
        );
        if (event.url) download(event.url);
      }),
    onError: (err) => {
      if (isInsufficientCredits(err)) {
        trackEvent('insufficient_credits_hit', {
          feature: 'Report deck',
          room_id: roomId ?? undefined,
        });
      }
      trackEvent('deck_failed', {
        reason: isInsufficientCredits(err) ? 'credits' : 'error',
        room_id: roomId ?? undefined,
      });
    },
    onSuccess: () => {
      trackEvent('deck_built', { room_id: roomId ?? undefined });
    },
    onSettled: () => {
      invalidateCredits(queryClient);
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/** Where a room's report lives in the app. */
export function reportPath(room: { id: string; sessionId: string }): string {
  return `/sessions/${room.sessionId}/report/${room.id}`;
}
